import { isEmpty, sortBy } from 'lodash-es';
import { type MouseEventHandler, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { useTypedParams } from 'react-router-typesafe-routes';
import { type Components, Virtuoso } from 'react-virtuoso';
import { AvatarWithStatus } from '@/components/common/avatar-with-status';
import SearchInput from '@/components/common/search-input';
import PageLayout from '@/components/layouts/page-layout/page-layout';
import useFriends from '@/hooks/contacts/use-friends';
import useBanGroupMember from '@/hooks/groups/use-ban-group-member';
import useChangeGroupMemberRole from '@/hooks/groups/use-change-group-member-role';
import useGroupInfo from '@/hooks/groups/use-group-info';
import useKickGroupMember from '@/hooks/groups/use-kick-group-member';
import useSearchGroupMembers, { type SearchGroupMember } from '@/hooks/groups/use-search-group-members';
import useTransferGroup from '@/hooks/groups/use-transfer-group';
import { useIsDesktop } from '@/hooks/use-user-platform';
import { useIsInternalUser } from '@/hooks/users/use-user-info';
import { Role } from '@/models/group/group-info';
import ROUTES from '@/routes';
import { useUserId } from '@/stores/auth';
import { makePrompt } from '@/stores/prompter';
import cn from '@/utils/merge-class-names';
import { GroupSettingHeader } from './settings';

const components: Components<SearchGroupMember> = {
  Footer: () => <div className='pb-safe' />,
};

function AddMemberLink({ className }: { className?: string }) {
  const { t } = useTranslation();
  return (
    <Link
      to={ROUTES.MESSAGES.GROUPS.SETTINGS.MEMBERS.$.INVITE.$path({ relative: true })}
      className={cn('text-inherit p-2 btn btn-ghost btn-sm', className)}>
      {t('common.add')}
    </Link>
  );
}

export default function GroupMembersIndexRoute() {
  const { id: groupId } = useTypedParams(ROUTES.MESSAGES.GROUPS.SETTINGS.MEMBERS);

  const { t } = useTranslation();
  const [keyword, setKeyword] = useState('');

  const userId = useUserId();
  const isDesktop = useIsDesktop();
  const { isInternalUser } = useIsInternalUser();
  const { mutateAsync: banMember } = useBanGroupMember(groupId);
  const { mutateAsync: kickOutMember } = useKickGroupMember(groupId);
  const { friends } = useFriends();
  const { canManage, data: groupInfo, isGroupOwner } = useGroupInfo(groupId);
  const { mutateAsync: changeGroupMemberRole } = useChangeGroupMemberRole(groupId);
  const { mutateAsync: transferGroup } = useTransferGroup(groupId);

  const filteredMembers = useSearchGroupMembers(groupId, keyword);

  const handleBanMember =
    (memberId: number, unban = false): MouseEventHandler =>
    async (e) => {
      e.preventDefault();
      e.stopPropagation();
      return banMember({ userId: memberId, ban: unban ? 'unban' : 'ban' }).catch(console.warn);
    };

  const handleKickOutMember =
    (memberId: number): MouseEventHandler =>
    async (e) => {
      e.preventDefault();
      e.stopPropagation();
      return makePrompt({
        key: 'kick-out-member',
        type: 'dialog',
        title: t('conversation.setting.group.remove'),
        content: t('conversation.kick-out-confirm-message'),
        resolveButtonLabel: t('conversation.setting.group.remove'),
        promise: () => kickOutMember({ userId: memberId, purgeMessages: false }),
      });
    };

  const handleEditMemberRole =
    (member: SearchGroupMember): MouseEventHandler =>
    async (e) => {
      e.preventDefault();
      if (userId === member.UserID || member.Role === Role.Owner) return;
      const resolutionButtons = [];
      if (isGroupOwner) {
        resolutionButtons.push({
          label: t('conversation.setting.group.admin.transfer-owner'), // 转让为群主
          resolution: 'transfer-owner',
        });
      }

      if (member.Role === Role.Admin) {
        resolutionButtons.push({
          label: t('conversation.setting.group.admin.to-member'), // 设为普通成员
          resolution: 'to-member',
        });
      }
      if (isEmpty(resolutionButtons)) return;

      makePrompt({
        key: 'member-role',
        type: 'action-sheet',
        title: '',
        resolutionButtons,
      }).then(async (resolution) => {
        switch (resolution) {
          case 'to-member':
            await changeGroupMemberRole({ userId: member.UserID, role: 0 });
            break;
          case 'transfer-owner':
            if (isGroupOwner) await transferGroup(member.UserID);
            break;
        }
      });
    };

  return (
    <PageLayout
      title={t('conversation.setting.group.members')}
      headerConfig={{
        className: 'desktop:hidden',
        navigateBack: true,
        actions: [<AddMemberLink key='add-member' />],
      }}>
      {isDesktop && (
        <>
          <GroupSettingHeader groupId={groupId} />
          <div className='justify-between  flex px-4 text-body1 h-12 items-center'>
            <span>{t('conversation.setting.group.id')}</span>
            <span className='text-secondary'>{groupInfo?.GroupData?.GroupCode}</span>
          </div>
        </>
      )}
      <SearchInput
        icon
        keyword={keyword}
        className='desktop:mx-0 desktop:my-2 mx-5 my-4'
        setKeyword={setKeyword}
        clearKeyword={keyword ? () => setKeyword('') : undefined}
      />

      <h2 className='mx-5 font-bold text-body1 flex justify-between items-center desktop:mx-4'>
        <span>{`${t('conversation.setting.group.member')} (${filteredMembers.length})`}</span>
        <AddMemberLink className='hidden desktop:inline' />
      </h2>

      <Virtuoso<SearchGroupMember>
        data={sortBy(filteredMembers, (m) => -m.Role)}
        overscan={6}
        defaultItemHeight={56}
        increaseViewportBy={168}
        components={components}
        className='flex flex-1 flex-col items-stretch'
        itemContent={(_, member) => (
          <div
            key={member.ID}
            onClick={handleEditMemberRole(member)}
            className='w-full px-5 py-2 inline-block'>
            <div className='flex items-center gap-2'>
              <div className='flex min-w-0 flex-1 items-center gap-4'>
                <AvatarWithStatus
                  imageUrl={member.UserHeadImg}
                  label={member.UserNickName}
                  className='size-12'
                  size={[48, 48]}
                  defaultAvatar='group'
                  isOnline={isInternalUser && member.OnLine === 1}
                  isFriend={isInternalUser && canManage && Object.keys(friends).includes(member.UserID.toString())}
                />

                <div className='flex flex-col items-stretch justify-center gap-0.5 overflow-hidden'>
                  <span className='truncate break-all text-h2 desktop:text-body1'>
                    {member.RemarkName || member.GroupMemberName || member.UserNickName}
                  </span>
                  {isInternalUser && member.OnlineDesc && (
                    <span className='text-xs text-metal-500'>{member.OnlineDesc}</span>
                  )}
                </div>
              </div>

              {canManage && member.Role < 9 && (
                <>
                  <button
                    type='button'
                    className='btn btn-warning btn-sm text-inverse'
                    onClick={handleBanMember(member.UserID, member.Status === 0)}>
                    {member.Status === 0
                      ? t('conversation.setting.group.has-banned')
                      : t('conversation.setting.group.ban')}
                  </button>

                  <button
                    type='button'
                    className='btn btn-error btn-sm'
                    onClick={handleKickOutMember(member.UserID)}>
                    {t('conversation.setting.group.remove')}
                  </button>
                </>
              )}

              {member.Role >= Role.Admin && (
                <span
                  className={cn('badge badge-outline badge-lg min-w-16 text-xs', {
                    'text-secondary': member.Role === Role.Owner,
                    'text-primary': member.Role === Role.Admin,
                  })}>
                  {member.Role === Role.Admin && t('conversation.setting.group.administrator')}
                  {member.Role === Role.Owner && t('conversation.setting.group.owner')}
                </span>
              )}
            </div>
          </div>
        )}
      />
    </PageLayout>
  );
}
